import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Timeline,
  Payment,
  AdminPanelSettings,
  Home,
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  AccountCircle,
  Logout
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { afterHoursTokens } from '../theme';
import { APP_STORE_URL } from '../constants/links';

interface SiteNavigationProps {
  currentSite?: 'main' | 'stats' | 'billing' | 'admin';
  onSignOut?: () => void;
  onSignIn?: () => void;
}

// Links shown in the public header; the home page owns the matching section ids.
const mainPageLinks = [
  { label: 'The app', href: '#features' },
  { label: 'Plans', href: '#pricing' },
  { label: 'Support', href: '/support.html' }
];

interface NavSite {
  id: 'main' | 'stats' | 'billing' | 'admin';
  name: string;
  url: string;
  icon: React.ReactNode;
  description: string;
  requiredRole?: string;
}

const navSites: NavSite[] = [
  {
    id: 'main',
    name: 'Main',
    url: '/',
    icon: <Home />,
    description: 'App download and information'
  },
  {
    id: 'stats',
    name: 'Statistics',
    url: '/stats',
    icon: <Timeline />,
    description: 'Global leaderboards and user statistics'
  },
  {
    id: 'billing',
    name: 'Billing',
    url: '/billing',
    icon: <Payment />,
    description: 'Payment management and subscriptions'
  },
  {
    id: 'admin',
    name: 'Admin',
    url: '/admin',
    icon: <AdminPanelSettings />,
    description: 'User/Team/Org management and admin tools',
    requiredRole: 'admin'
  }
];

export const SiteNavigation: React.FC<SiteNavigationProps> = ({
  currentSite,
  onSignOut,
  onSignIn
  }) => {
    const { user } = useAuth();
    const {
      userRole,
      hasMinimumRole,
    isWebsiteAdmin
  } = useRole();
    const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [profileAnchor, setProfileAnchor] = React.useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const { mode, toggleMode } = useThemeMode();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleProfileOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchor(null);
  };

  const handleSiteNavigation = (url: string) => {
    // Navigate in the same window for seamless experience
    navigate(url);
    handleMenuClose();
  };

  const getCurrentSiteInfo = () => {
    return navSites.find(site => site.id === currentSite);
  };

  const getAvailableSites = () => {
    return navSites.filter(site => {
      // Don't show current site
      if (site.id === currentSite) return false;
      
      // Check role requirements
      if (site.requiredRole) {
        if (site.requiredRole === 'admin') {
          // Match the strict rules: profile roles alone do not grant site administration.
          const hasWebsiteAdmin = isWebsiteAdmin();
          if (!hasWebsiteAdmin) {
            return false;
          }
        } else {
          // For other sites, use the original role check
          if (!hasMinimumRole(site.requiredRole as any)) {
            return false;
          }
        }
      }
      
      return true;
    });
  };

  const currentSiteInfo = getCurrentSiteInfo();
  const availableSites = getAvailableSites();
  const isMainSite = currentSite === 'main';

  return (
    <AppBar
      position="static"
      sx={{ mb: isMainSite ? 0 : { xs: 1, sm: 2 } }}
      className="transition-colors"
    >
      <Toolbar sx={{ px: { xs: 2, sm: 3, md: isMainSite ? 7 : 3 }, minHeight: { xs: 64, md: isMainSite ? 84 : 68 } }}>
        {/* JuggleVision Brand */}
        <Box
          component={RouterLink}
          to="/"
          aria-label="JuggleVision home"
          sx={{
            display: 'flex',
            alignItems: 'center',
            mr: { xs: 1, sm: 3 },
            color: 'inherit',
            textDecoration: 'none'
          }}
        >
          {/* App Icon */}
          <Box
            sx={{
              width: { xs: 34, sm: 40 },
              height: { xs: 34, sm: 40 },
              backgroundImage: 'url(/app-icon.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: { xs: 1.5, sm: 2 },
              mr: { xs: 1, sm: 1.5 }
            }}
          />

          {/* Wordmark: hidden on phones once account controls need the room */}
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.5rem' },
              letterSpacing: '-0.01em',
              color: 'text.primary',
              display: { xs: user ? 'none' : 'block', sm: 'block' }
            }}
          >
            JuggleVision
            <Box component="sup" aria-hidden="true" sx={{ fontSize: '0.5em', fontWeight: 500, ml: '1px' }}>™</Box>
          </Typography>
        </Box>

        {/* Public page links */}
        {isMainSite && (
          <Box
            component="nav"
            aria-label="Main navigation"
            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 4, ml: 4 }}
          >
            {mainPageLinks.map((link) => (
              <Box
                component="a"
                key={link.label}
                href={link.href}
                sx={{
                  color: 'text.primary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  py: 1.5,
                  '&:hover': { color: afterHoursTokens[mode].link }
                }}
              >
                {link.label}
              </Box>
            ))}
          </Box>
        )}

        {/* Current Site Info (the public page shows its own links instead) */}
        {currentSiteInfo && !isMainSite && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              {currentSiteInfo.icon}
              <Typography variant="h6" sx={{ ml: 1, mr: 2 }}>
                {currentSiteInfo.name}
              </Typography>
            </Box>
            <Chip 
              label={currentSiteInfo.id === 'main' ? 'Home' : currentSiteInfo.name}
              size="small" 
              color="secondary"
              sx={{ 
                display: { xs: 'flex', md: 'none' },
                fontSize: { xs: '0.75rem' },
                height: { xs: 24, sm: 28 }
              }}
            />
          </Box>
        )}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* User Info */}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 1, sm: 2 } }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mr: 1, 
                display: { xs: 'none', lg: 'block' },
                fontSize: { sm: '0.875rem' }
              }}
            >
              {user.email}
            </Typography>
            {userRole && (
              <Chip 
                label={userRole} 
                size="small" 
                color="primary"
                sx={{ 
                  mr: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  height: { xs: 24, sm: 28 }
                }}
              />
            )}
          </Box>
        )}

        {/* Site Navigation Menu */}
          {availableSites.length > 0 && (
            <>
              <Button
                color="inherit"
                aria-label="Sites"
                startIcon={<MenuIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
              onClick={handleMenuOpen}
              sx={{
                mr: { xs: 0.5, sm: 1 },
                px: { xs: 1, sm: 2 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minWidth: { xs: 'auto', sm: 'auto' }
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Sites</Box>
            </Button>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: { mt: 1, minWidth: 280 }
              }}
            >


              {availableSites.map((site) => (
                <MenuItem
                  key={site.id}
                  onClick={() => handleSiteNavigation(site.url)}
                  sx={{ py: 1.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      {site.icon}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {site.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {site.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}

              
              </Menu>
            </>
          )}

          {/* Theme Toggle */}
          <IconButton
            color="inherit"
            onClick={toggleMode}
            sx={{ mr: { xs: 0.5, sm: 1 } }}
            aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            size="small"
          >
            {mode === 'dark' ? (
              <Brightness7 sx={{ fontSize: { xs: 18, sm: 20 } }} />
            ) : (
              <Brightness4 sx={{ fontSize: { xs: 18, sm: 20 } }} />
            )}
          </IconButton>

          {/* Sign In (public page only) */}
          {!user && onSignIn && (
            <Button
              color="inherit"
              onClick={onSignIn}
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, mr: 1, fontWeight: 500 }}
            >
              Sign in
            </Button>
          )}

          {/* App Store button (public page only) */}
          {isMainSite && (
            <Button
              component="a"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              sx={{ display: { xs: 'none', md: 'inline-flex' }, minHeight: 48, px: 3, mr: user ? 1 : 0 }}
            >
              Get the iOS app
            </Button>
          )}

          {/* Sign Out Button */}
          {user && onSignOut && (
            <Button
              color="inherit"
              variant="outlined"
              onClick={onSignOut}
              size="small"
              sx={{ mr: { xs: 0.5, sm: 1 } }}
            >
              Sign Out
            </Button>
          )}

          {/* User Avatar Menu */}
          {user && (
            <>
              <IconButton onClick={handleProfileOpen} color="inherit" sx={{ p: 0.5 }}>
                <Avatar src={user.photoURL || undefined} sx={{ width: 32, height: 32 }}>
                  {user.email?.[0].toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={profileAnchor}
                open={Boolean(profileAnchor)}
                onClose={handleProfileClose}
                PaperProps={{ sx: { mt: 1 } }}
              >
                {/* Profile lives in the Stats app; there is no separate profile or settings page. */}
                <MenuItem onClick={() => { handleProfileClose(); navigate('/stats'); }}>
                  <ListItemIcon>
                    <AccountCircle fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Stats & profile" />
                </MenuItem>
                {onSignOut && (
                  <MenuItem onClick={() => { handleProfileClose(); onSignOut(); }}>
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Sign Out" />
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
      </Toolbar>
    </AppBar>
  );
};

export default SiteNavigation;
